import {
  CircularProgress,
  CircularProgressProps,
  LinearProgress,
  LinearProgressProps,
} from '@mui/material';
import { useEffect, useState } from 'react';

interface DeterminateLoaderProps {
  duration: number; // in milliseconds
  updateInterval?: number; // in milliseconds
  maxProgress?: number;
  isCompleted: boolean;
  onComplete: () => void;
  variant?: 'circular' | 'linear';
  circularProgressProps?: CircularProgressProps;
  linearProgressProps?: LinearProgressProps;
  onProgressUpdate?: (progress: number) => void;
  completeDelay?: number;
}

export const DeterminateLoader = ({
  duration,
  updateInterval = 500,
  maxProgress = 80,
  isCompleted,
  onComplete,
  variant = 'circular',
  circularProgressProps,
  linearProgressProps,
  onProgressUpdate,
  completeDelay = 1000,
}: DeterminateLoaderProps) => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    onProgressUpdate?.(progress);
  }, [onProgressUpdate, progress]);

  useEffect(() => {
    if (!isCompleted) {
      return;
    }

    setProgress(100);
    const timer = setTimeout(() => {
      onComplete();
    }, completeDelay);

    return () => {
      clearTimeout(timer);
    };
  }, [completeDelay, isCompleted, onComplete]);

  useEffect(() => {
    if (isCompleted) {
      return;
    }

    const timer = setInterval(() => {
      setProgress((prevProgress) => {
        if (prevProgress >= maxProgress) {
          clearInterval(timer);
          return prevProgress;
        }
        const newProgress = prevProgress + (100 * updateInterval) / duration;
        return Math.min(newProgress, maxProgress);
      });
    }, updateInterval);

    return () => {
      clearInterval(timer);
    };
  }, [duration, updateInterval, maxProgress, isCompleted]);

  if (variant === 'linear') {
    return (
      <LinearProgress
        {...linearProgressProps}
        variant="determinate"
        value={progress}
      />
    );
  }

  return (
    <CircularProgress
      size={24}
      {...circularProgressProps}
      variant="determinate"
      value={progress}
    />
  );
};
